import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  ErrorInfo,
  ReactNode,
  lazy,
  Suspense,
} from 'react'
import { useParams } from 'react-router-dom'

const DocumentEditor = lazy(() =>
  import('@onlyoffice/document-editor-react').then(m => ({ default: m.DocumentEditor })),
)
import fetchAPI, { IHttpError } from '../../../lib/utils/fetch-api'
import B2BLayout from '../../layouts/b2b'
import { App } from 'antd'

type FileType = 'docx' | 'xls' | 'xlsx'
type EditorType = 'word' | 'cell'

interface DocumentPayload {
  id: number | string
  name?: string
  url?: string
  type?: string
}

const detectFileType = (urlOrType?: string): { type: FileType; editorType: EditorType } => {
  const value = (urlOrType || '').toLowerCase()
  if (value.includes('.docx') || value === 'docx' || value === 'doc') {
    return { type: 'docx', editorType: 'word' }
  }
  if (value.includes('.xlsx') || value === 'xlsx') return { type: 'xlsx', editorType: 'cell' }
  if (value.includes('.xls') || value === 'xls') return { type: 'xls', editorType: 'cell' }
  return { type: 'docx', editorType: 'word' }
}

// Локальный ErrorBoundary для отображения деталей ошибки
interface DocumentEditorErrorBoundaryProps {
  children: ReactNode
  onError: (error: Error, errorInfo: ErrorInfo) => void
}

class DocumentEditorErrorBoundary extends React.Component<
  DocumentEditorErrorBoundaryProps,
  { hasError: boolean; error: Error | null; errorInfo: ErrorInfo | null }
> {
  constructor(props: DocumentEditorErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ error, errorInfo })
    this.props.onError(error, errorInfo)
    console.error('Ошибка в DocumentEditor:', error, errorInfo)
  }

  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <div
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'white',
            borderRadius: '8px',
            padding: '24px',
            gap: '8px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '60px', marginBottom: '10px' }}>⚠️</div>
          <h3 style={{ color: '#dc3545' }}>Ошибка при загрузке редактора</h3>
          <p style={{ color: '#6c757d', maxWidth: '500px', marginBottom: '8px' }}>
            {this.state.error.message || 'Произошла непредвиденная ошибка'}
          </p>
          {this.state.error.stack && (
            <details
              style={{
                maxWidth: '600px',
                textAlign: 'left',
                marginTop: '16px',
                padding: '12px',
                background: '#f5f5f5',
                borderRadius: '4px',
                fontSize: '12px',
                maxHeight: '200px',
                overflow: 'auto',
              }}
            >
              <summary style={{ cursor: 'pointer', marginBottom: '8px' }}>Детали ошибки</summary>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {this.state.error.stack}
              </pre>
            </details>
          )}
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '8px 16px',
              backgroundColor: '#1890ff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              marginTop: '16px',
            }}
          >
            Обновить страницу
          </button>
        </div>
      )
    }

    return this.props.children
  }
}

interface EditorContentProps {
  id: string
  loading: boolean
  error: string | null
  setError: (error: string | null) => void
  documentConfig: any
  serverUrl: string
  documentKeyRef: React.MutableRefObject<string | null>
  hadUnsavedChangesRef: React.MutableRefObject<boolean>
  editorReadyRef: React.MutableRefObject<boolean>
}

const EditorContent: React.FC<EditorContentProps> = ({
  id,
  loading,
  error,
  setError,
  documentConfig,
  serverUrl,
  documentKeyRef,
  hadUnsavedChangesRef,
  editorReadyRef,
}) => {
  const { message } = App.useApp()

  const renderPlaceholder = (content: React.ReactNode) => (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'white',
        borderRadius: '8px',
        padding: '24px',
        gap: '8px',
        textAlign: 'center',
      }}
    >
      {content}
    </div>
  )

  const renderContent = () => {
    if (!serverUrl) {
      return renderPlaceholder(
        <>
          <div style={{ fontSize: '60px', marginBottom: '10px' }}>⚠️</div>
          <h3 style={{ color: '#dc3545' }}>Document server недоступен</h3>
          <p style={{ color: '#6c757d', maxWidth: '400px' }}>Обратитесь к администратору</p>
        </>,
      )
    }

    if (error) {
      return renderPlaceholder(
        <>
          <div style={{ fontSize: '60px', marginBottom: '10px' }}>⚠️</div>
          <h3 style={{ color: '#dc3545' }}>Ошибка загрузки документа</h3>
          <p style={{ color: '#6c757d', maxWidth: '420px', marginBottom: '16px' }}>{error}</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '8px 16px',
              backgroundColor: '#1890ff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Обновить страницу
          </button>
        </>,
      )
    }

    if (loading || !documentConfig) {
      return renderPlaceholder(
        <>
          <div style={{ fontSize: '32px', marginBottom: '10px' }}>⏳</div>
          <p style={{ color: '#6c757d' }}>Загружаем документ...</p>
        </>,
      )
    }

    return (
      <DocumentEditorErrorBoundary
        onError={(error, errorInfo) => {
          const errorMessage = `Ошибка рендеринга редактора: ${error.message}`
          setError(errorMessage)
          console.error('Ошибка в DocumentEditor компоненте:', error, errorInfo)
        }}
      >
        <Suspense
          fallback={renderPlaceholder(
            <>
              <div style={{ fontSize: '32px', marginBottom: '10px' }}>⏳</div>
              <p style={{ color: '#6c757d' }}>Загружаем редактор документов...</p>
            </>,
          )}
        >
          <div
            style={{
              height: '100%',
              borderRadius: 0,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <DocumentEditor
              key={documentKeyRef.current || `editor-${id}`}
              id="onlyoffice-editor"
              documentServerUrl={serverUrl}
              config={documentConfig}
              width="100%"
              height="100%"
              events_onAppReady={() => {
                editorReadyRef.current = true
              }}
              events_onError={e => {
                console.error('Ошибка редактора OnlyOffice:', e)
                const errorData = e as any
                let errorMessage = 'Произошла ошибка в редакторе документа'

                if (errorData?.data) {
                  const errorCode = errorData.data.errorCode
                  const errorDescription = errorData.data.errorDescription

                  console.error('Код ошибки:', errorCode)
                  console.error('Описание:', errorDescription)

                  // Обработка специфичных ошибок OnlyOffice
                  switch (errorCode) {
                    case 0:
                      errorMessage = 'Документ не найден или недоступен'
                      break
                    case 1:
                      errorMessage = 'Ошибка при открытии документа'
                      break
                    case 2:
                      errorMessage = 'Ошибка при сохранении документа'
                      break
                    case 3:
                      errorMessage = 'Документ недоступен для редактирования'
                      break
                    case 4:
                    case -4:
                      errorMessage =
                        'Ошибка при загрузке документа с сервера. Проверьте доступность файла по URL.'
                      break
                    case 6:
                      errorMessage = 'Ошибка при сохранении документа на сервер'
                      break
                    case 7:
                      errorMessage = 'Документ заблокирован для редактирования'
                      break
                    default:
                      if (errorDescription) {
                        errorMessage = `Ошибка редактора: ${errorDescription}`
                      } else {
                        errorMessage = `Ошибка редактора (код: ${errorCode})`
                      }
                  }
                }

                setError(errorMessage)
                message.error(errorMessage)
              }}
              events_onWarning={e => {
                console.warn('Предупреждение редактора OnlyOffice:', e)
                const warningData = e as any
                if (warningData?.data) {
                  console.warn('Код предупреждения:', warningData.data.warningCode)
                  console.warn('Описание:', warningData.data.warningDescription)
                }
              }}
              events_onDocumentReady={() => {
                console.log('Документ успешно загружен в редактор')
                setError(null)
              }}
              events_onDocumentStateChange={async e => {
                const state = (e as any)?.data
                const hasUnsavedChanges = typeof state === 'boolean' ? state : Boolean(state)
                if (!hasUnsavedChanges && hadUnsavedChangesRef.current) {
                  message.success('Изменения успешно сохранены')
                }
                hadUnsavedChangesRef.current = hasUnsavedChanges
              }}
            />
          </div>
        </Suspense>
      </DocumentEditorErrorBoundary>
    )
  }

  return (
    <div
      style={{
        flex: 1,
        position: 'relative',
        minHeight: '600px',
      }}
    >
      {renderContent()}
    </div>
  )
}

const OnlyOfficeYandexCloudEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const [documentData, setDocumentData] = useState<DocumentPayload | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const hadUnsavedChangesRef = useRef(false)
  const editorReadyRef = useRef(false)
  const editorInstanceRef = useRef<any>(null)

  // URL сервера OnlyOffice
  const serverUrl = process.env?.REACT_APP_ONLYOFFICE_URL || 'https://documentserver.in.ze-okna.ru/'
  // для локалки 'http://host.docker.internal:7070' // window.location.origin
  // для бека / прода window.location.origin
  const callbackBaseUrl = process.env?.REACT_APP_ONLYOFFICE_CALLBACK_URL || window.location.origin

  useEffect(() => {
    if (!id || !parseInt(id)) {
      setError('Не передан идентификатор документа')
      setDocumentData(null)
      return
    }

    const controller = new AbortController()
    const fetchDocument = async () => {
      try {
        setLoading(true)
        setError(null)

        await fetchAPI(`/api/get_document/${id}`)
          .then(payload => {
            if (controller.signal.aborted) return

            if (!payload) {
              setError('Документ не найден')
              setDocumentData(null)
              return
            }

            const { id: docId, name, url, type } = payload

            // Проверяем наличие обязательных полей
            if (!url) {
              setError('URL документа не указан. Файл не может быть загружен.')
              setDocumentData(null)
              return
            }

            if (!name) {
              setError('Имя документа не указано')
              setDocumentData(null)
              return
            }

            setDocumentData({ id: docId, name, url, type })
          })
          .catch(e => {
            if (controller.signal.aborted) return

            const httpError = e as IHttpError
            let errorMessage = 'Неизвестная ошибка'

            // Обработка различных типов ошибок
            if (httpError?.statusCode === 404) {
              errorMessage = 'Документ не найден. Возможно, он был удален или не существует.'
            } else if (httpError?.statusCode === 403) {
              errorMessage = 'Нет доступа к документу. Обратитесь к администратору.'
            } else if (httpError?.statusCode === 500) {
              errorMessage = 'Ошибка сервера при загрузке документа. Попробуйте позже.'
            } else if (httpError?.statusCode === 0 || httpError?.message?.includes('fetch')) {
              errorMessage = 'Не удалось подключиться к серверу. Проверьте подключение к интернету.'
            } else if (httpError?.message) {
              errorMessage = httpError.message
            }

            console.error('Ошибка загрузки документа:', httpError)
            setError(errorMessage)
            setDocumentData(null)
          })
          .finally(() => {
            if (!controller.signal.aborted) {
              setLoading(false)
            }
          })
      } catch (err) {
        if (controller.signal.aborted) return
        console.error('Не удалось загрузить документ', err)
        setDocumentData(null)
        setError(
          err instanceof Error
            ? err.message
            : 'Неизвестная ошибка загрузки документа. Попробуйте обновить страницу.',
        )
        setLoading(false)
      }
    }
    fetchDocument()
    return () => controller.abort()
  }, [id])

  const documentKeyRef = useRef<string | null>(null)

  // Проверяем данные перед созданием конфигурации
  useEffect(() => {
    if (documentData) {
      if (!documentData.url) {
        setError('URL документа не указан. Файл не может быть загружен.')
      } else if (!documentData.name) {
        setError('Имя документа не указано')
      }
    }
  }, [documentData])

  const documentConfig = useMemo(() => {
    // Проверяем наличие всех необходимых данных
    if (!documentData) return null
    if (!documentData.url || !documentData.name) {
      return null
    }

    const fileInfo = detectFileType(documentData.type || documentData.url)
    const userId = localStorage.getItem('userId') || 'user-001'
    const userName = localStorage.getItem('userName') || 'Пользователь'
    const baseUrl = callbackBaseUrl.replace(/\/+$/, '')
    const callbackUrl = `${baseUrl}/api/get_document/${documentData.id}/callback`

    if (!documentKeyRef.current) {
      documentKeyRef.current = `${id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    }

    return {
      document: {
        fileType: fileInfo.type,
        key: documentKeyRef.current,
        title: documentData.name,
        url: documentData.url,
      },
      documentType: fileInfo.editorType,
      editorConfig: {
        mode: 'edit' as const,
        lang: 'ru' as const,
        user: {
          id: userId,
          name: userName,
        },
        callbackUrl,
        plugins: {
          autostart: ['asc.{A1B2C3D4-E5F6-4711-8899-001122334455}'],
        },
        customization: {
          autosave: true,
          forcesave: false,
          toolbar: true,
          hideRightMenu: true,
          hideFileMenu: true,
          features: {
            spellcheck: true,
          },
          goback: false,
          compactHeader: false,
          compactToolbar: false,
          hideRuler: false,
          macros: false,
        },
      },
    }
  }, [callbackBaseUrl, documentData, id])

  useEffect(() => {
    if (documentData?.id) {
      documentKeyRef.current = null
    }
  }, [documentData?.id])

  useEffect(() => {
    return () => {
      editorReadyRef.current = false
      editorInstanceRef.current = null
      const editorFrame = document.getElementById('onlyoffice-editor') as HTMLIFrameElement
      if (editorFrame) {
        try {
          editorFrame.src = 'about:blank'
        } catch (e) {
          console.debug('Ошибка очистки iframe:', e)
        }
      }
    }
  }, [])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && editorReadyRef.current) {
        setTimeout(() => {
          try {
            const editorFrame = document.getElementById('onlyoffice-editor') as HTMLIFrameElement
            if (editorFrame?.contentWindow) {
              editorFrame.contentWindow.focus()
            }
          } catch (e) {
            console.debug('Не удалось установить фокус на iframe редактора:', e)
          }
        }, 100)
      }
    }

    const handleWindowFocus = () => {
      if (editorReadyRef.current) {
        setTimeout(() => {
          try {
            const editorFrame = document.getElementById('onlyoffice-editor') as HTMLIFrameElement
            if (editorFrame?.contentWindow) {
              editorFrame.contentWindow.focus()
            }
          } catch (e) {
            console.debug('Не удалось установить фокус на iframe редактора:', e)
          }
        }, 100)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleWindowFocus)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleWindowFocus)
    }
  }, [])

  const pageTitle = 'Редактор документа'

  const breadcrumbs = useMemo(
    () => [
      {
        href: '/document-templates',
        title: 'Шаблоны документов',
      },
      {
        title: pageTitle,
      },
    ],
    [pageTitle],
  )

  return (
    <App>
      <div
        style={{
          position: 'relative',
        }}
      >
        <B2BLayout breadcrumbs={breadcrumbs} pageTitle={pageTitle}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: 'calc(100vh - 90px)',
              position: 'relative',
            }}
          >
            <EditorContent
              id={id || ''}
              loading={loading}
              error={error}
              setError={setError}
              documentConfig={documentConfig}
              serverUrl={serverUrl}
              documentKeyRef={documentKeyRef}
              hadUnsavedChangesRef={hadUnsavedChangesRef}
              editorReadyRef={editorReadyRef}
            />
          </div>
        </B2BLayout>
      </div>
    </App>
  )
}

export default OnlyOfficeYandexCloudEditor
